import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components/macro'

import { useCookie } from 'core/ui/hooks/useCookie'

import { Modal } from 'legacy/components/ModalV2/Modal'
import { addPreferredLane } from 'legacy/services/preferredLanes'
import { screen } from 'legacy/styles/screen'
import { useQueryParams } from 'legacy/utils/hooks/useQueryParams'

import { useAnalytics } from '../hooks/useAnalytics'
import * as loadUtils from '../utils/loadUtils'
import { LoginDialog } from './LoginDialog'
import { SignupDialogForm } from './SignupDialogForm'
import { useWindowLeave } from './useWindowLeave'

const Container = styled.div`
  width: 100%;
  padding: 20px 24px;

  ${screen.md} {
    max-width: 520px;
  }
`

const IN_5_SECONDS = 5 * 1000
const IN_30_SECONDS = 30 * 1000
const IN_30_MINUTES = 1 / 48

const TriggerAction = {
  DropOff: 'drop_off',
  TimeOut: 'time_out',
}

function OutletDialog(props) {
  const { load, lane } = props
  const [isLogin, setLogin] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [cookie, setCookie] = useCookie('lb.sawOutletDialog', { expires: IN_30_MINUTES })
  const { mc, dot, email } = useQueryParams()
  const { analytics } = useAnalytics()

  const loadUUID = load?.uuid
  const isLoadTaken = loadUtils.isLoadTaken(load)
  const shouldDisplayDialog = !cookie

  function handleClose() {
    return setOpen(false)
  }

  function toggleLogin() {
    return setLogin(cur => !cur)
  }

  const openDialog = useCallback(
    ({ triggerAction }) => {
      setOpen(true)
      setCookie(true)

      analytics.track(['Outlet Signup', 'View'], {
        loadTaken: isLoadTaken,
        loadUUID,
        triggerAction,
        preferredLane: Boolean(lane),
      })
      analytics.tag(['Outlet Signup View'])
    },
    [analytics, isLoadTaken, lane, loadUUID, setCookie]
  )

  const handleWindowLeave = useCallback(() => {
    if (shouldDisplayDialog) {
      openDialog({ triggerAction: TriggerAction.DropOff })
    }
  }, [openDialog, shouldDisplayDialog])

  const handleSuccessView = useCallback(() => {
    analytics.track(['Outlet Signup', 'Complete'], {
      loadUUID,
    })
    analytics.tag(['Outlet Signup Completed'])
  }, [analytics, loadUUID])

  const handleResendEmail = useCallback(() => {
    analytics.track(['Outlet Signup', 'Re-send Email'], {
      loadUUID,
    })
    analytics.tag(['Outlet Signup Re-send Email Click'])
  }, [analytics, loadUUID])

  async function onLogin() {
    if (lane) {
      try {
        await addPreferredLane(lane)
      } catch (error) {
        /* do nothing, we will ignore these errors */
      }
    }

    window.location.reload()
  }

  useWindowLeave(handleWindowLeave)

  useEffect(() => {
    let timeout
    const displayTimeout = isLoadTaken ? IN_5_SECONDS : IN_30_SECONDS

    if (shouldDisplayDialog) {
      timeout = setTimeout(() => {
        openDialog({ triggerAction: TriggerAction.TimeOut })
      }, displayTimeout)
    }

    return () => {
      timeout && clearTimeout(timeout)
    }
  }, [isLoadTaken, openDialog, shouldDisplayDialog])

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Container>
        {isLogin ? (
          <LoginDialog
            onClose={handleClose}
            onAuthChange={onLogin}
            onToggleLogin={toggleLogin}
            loadUUID={loadUUID}
            lane={lane}
          />
        ) : (
          <SignupDialogForm
            onClose={handleClose}
            isLoadTaken={isLoadTaken}
            onSuccessView={handleSuccessView}
            onResendEmail={handleResendEmail}
            onToggleLogin={toggleLogin}
            mc={mc}
            dot={dot}
            email={email}
            lane={lane}
          />
        )}
      </Container>
    </Modal>
  )
}

OutletDialog.propTypes = {
  load: PropTypes.shape({ active_offer: PropTypes.object }),
  lane: PropTypes.shape({
    origin: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    destination: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  }),
}

OutletDialog.defaultProps = {
  load: undefined,
  lane: undefined,
}

export { OutletDialog }
